import {useCallback, useContext, useMemo} from 'react';
import Favicon, {FaviconProps} from 'react-favicon';

import {themeConfig} from 'constants/theme';
import {UserContext} from 'context';
import {useObservable, useOnlineSessions} from 'hooks';
import {SessionResultStatus, SessionStatus} from 'interfaces';
import {onlineSessionsStore, userActiveSessionStore} from 'services';
import {sessionUtils} from 'utils';

const faviconPath = '/assets/favicon';
const icons = {
    default: `${faviconPath}/user-default.svg`,
    inSession: `${faviconPath}/in-session.svg`,
    inSessionMute: `${faviconPath}/in-session-mute.svg`,
    inSessionRecording: `${faviconPath}/in-session-recording.svg`,
    inSessionRecordingFailed: `${faviconPath}/in-session-recording-failed.svg`,
    sessionEndedSuccess: `${faviconPath}/session-ended-success.svg`,
    sessionEndedFailed: `${faviconPath}/session-ended-failed.svg`,
};

const MAX_ALERT_COUNT = 99; // to not show more than 2 digits as it doesn't make sense
const ICON_SIZE = 256; // used for better quality

export const UserFavicon: React.FC = () => {
    const userContext = useContext(UserContext);
    const {screenRecorderState} = useObservable(onlineSessionsStore);

    const {count: callingSessionCount} = useOnlineSessions({status: SessionStatus.Calling});
    const {count: newSessionCount} = useOnlineSessions({status: SessionStatus.New});
    const incomingSessionsCount = useMemo(() => newSessionCount + callingSessionCount, [callingSessionCount, newSessionCount]);
    const alertCount = useMemo(() => Math.min(incomingSessionsCount, MAX_ALERT_COUNT), [incomingSessionsCount]);

    const userActiveSessionStoreObservable = useObservable(userActiveSessionStore);
    const isInSession = useMemo(() => !!userActiveSessionStoreObservable?.session, [userActiveSessionStoreObservable]);

    const isInSessionMute = useMemo(() =>
        sessionUtils.hasActivityWithAudio(userActiveSessionStoreObservable?.session) &&
        !userActiveSessionStoreObservable?.audioEnabled,
    [userActiveSessionStoreObservable]);

    const isInSuccessfullyEndedSession = useMemo(() =>
        userActiveSessionStoreObservable?.session?.result.status === SessionResultStatus.Success,
    [userActiveSessionStoreObservable]);

    const isInFailedEndedSession = useMemo(() =>
        userActiveSessionStoreObservable?.session?.result?.status === SessionResultStatus.Failed,
    [userActiveSessionStoreObservable]);

    const requireScreenRecorder = useMemo(() => userContext?.settings?.userSession?.requireScreenRecorder, [userContext]);

    const isInSessionRecording = useMemo(() => isInSession && requireScreenRecorder && screenRecorderState?.isOnline,
        [isInSession, requireScreenRecorder, screenRecorderState?.isOnline]);

    const isInSessionRecordingFailed = useMemo(() => isInSession && requireScreenRecorder && !screenRecorderState?.isOnline,
        [isInSession, requireScreenRecorder, screenRecorderState?.isOnline]);

    const iconProps: FaviconProps = useMemo(() => {
        if (isInSuccessfullyEndedSession) {
            return {url: icons.sessionEndedSuccess};
        } else if (isInFailedEndedSession) {
            return {url: icons.sessionEndedFailed};
        } else if (isInSessionRecording) {
            return {url: icons.inSessionRecording};
        } else if (isInSessionRecordingFailed) {
            return {url: icons.inSessionRecordingFailed};
        } else if (isInSessionMute) {
            return {url: icons.inSessionMute};
        } else if (isInSession) {
            return {url: icons.inSession};
        }
        return {url: icons.default};
    }, [isInSuccessfullyEndedSession, isInFailedEndedSession, isInSessionRecording,
        isInSessionRecordingFailed, isInSessionMute, isInSession]);

    const renderOverlay = useCallback((canvas, context) => {
        if (!alertCount || isInSession) {
            return null;
        }

        // Create a rounded square taking 1/4 of the icon size:
        const squareSize = ICON_SIZE / 2;
        const fontSize = squareSize / 5 * 4;
        context.font = `bold ${fontSize}px "Open Sans"`;
        const textWidth = context.measureText(alertCount).width;
        const minHorizontalPadding = ICON_SIZE / 10;
        const fullTextWidth = textWidth + minHorizontalPadding * 2;

        const width = ICON_SIZE / 8;
        const radius = ICON_SIZE / 8;
        const top = canvas.height / 2;
        let left = canvas.width / 2;
        if (fullTextWidth > left) {
            left = canvas.width - fullTextWidth;
        }
        const resultHorizontalPadding = Math.max(minHorizontalPadding, (left - textWidth) / 2);
        const bottom = canvas.height;
        const right = canvas.width;

        context.fillStyle = themeConfig.colors.alert;
        context.strokeStyle = themeConfig.colors.white;
        context.lineWidth = 1;

        context.beginPath();
        context.moveTo(left + width, top);
        context.quadraticCurveTo(left, top, left, top + radius);
        context.lineTo(left, bottom);
        context.lineTo(right, bottom);
        context.quadraticCurveTo(right, bottom, right, bottom - radius);
        context.lineTo(right, top);
        context.closePath();
        context.fill();

        context.fillStyle = themeConfig.colors.white;
        context.textAlign = 'left';
        context.textBaseline = 'bottom';
        context.fillText(alertCount, left + resultHorizontalPadding, bottom);
    }, [alertCount, isInSession]);

    return (
        <Favicon {...iconProps} renderOverlay={renderOverlay} iconSize={ICON_SIZE} />
    );
};